import React, { useEffect, useState } from 'react';
import { useMount } from 'react-use';
import { useLocation } from 'react-router-dom';
import {
  Button,
  Switch,
  DatePicker,
  Select,
  Spin,
  Table,
  Popover,
  notification,
  Avatar,
  Tooltip
} from 'antd';
import { Comment } from '@ant-design/compatible';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '@ant-design/pro-layout';
import moment from 'moment';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import { Pie } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import api from '../../../../api/api';
import { configSelector, businessesSelector } from '../../../../atoms/Selectors';
import { randomColor, renderShortDate, thousand, toCurrency } from '../../../utils/functions';
import { profileAtom, timezoneAtom } from '../../../../atoms/Atoms';
import { dateRangePresets } from '../../../utils/utils';
import Grocefy from '../../../../assets/images/grocefyLogoAlone.png';
import envConfig from '../../../../envConfig';

function LossParticipation() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get('businessId');
  const type = queryParams.get('type');
  const lossType = queryParams.get('lossType');
  const showTotals = queryParams.get('showTotals');
  const timer = queryParams.get('timer');
  const config = useRecoilValueLoadable(configSelector);
  const { t, i18n } = useTranslation();
  const { RangePicker } = DatePicker;
  const { Option } = Select;
  const profile = useRecoilValue(profileAtom);
  const timezone = useRecoilValue(timezoneAtom);
  const [loading, setLoading] = useState(false);
  const [dataPoints, setDataPoints] = useState([]);
  const [selectedType, setSelectedType] = useState(type ?? '0');
  const [dates, setDates] = useState({
    end: dayjs().endOf('day').add(-1, 'day'),
    start: dayjs().endOf('day').add(-8, 'day').add(1, 'second')
  });
  const [business, setBusiness] = useState(null);
  const [selectedBusinessId, setSelectedBusinessId] = useState(businessId ?? '');
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const businesses = useRecoilValueLoadable(businessesSelector);
  const [selectedLossType, setSelectedLossType] = useState(lossType ?? 'cost');
  const [hideTotals, setHideTotals] = useState(showTotals !== 'true');
  const [refreshTimer, setRefreshTimer] = useState(timer ? parseInt(timer, 10) : 0);
  const [isInitialized, setIsInitialized] = useState(false);

  function showMessage(message) {
    notification.open({
      message: '',
      description: (
        <Comment
          author={<span>Grocefy</span>}
          avatar={<Avatar src={Grocefy} alt="grocefy" />}
          content={
            <p className="text-sm">
              {message}
            </p>
          }
          datetime={
            <Tooltip title={moment().format('YYYY-MM-DD HH:mm:ss')}>
              <span>{moment().fromNow()}</span>
            </Tooltip>
          }
        />
      ),
    });
  }

  function getData() {
    setLoading(true);
    let selectedTypeUrl = 'division';
    if (selectedType === '1') {
      selectedTypeUrl = 'department';
    } else if (selectedType === '2') {
      selectedTypeUrl = 'category';
    }
    api.get(`reports/loss/participation/${selectedTypeUrl}?startDate=${dates.start.format('YYYY-MM-DDTHH:mm:ssZ')}&endDate=${dates.end.format('YYYY-MM-DDTHH:mm:ssZ')}&businessId=${selectedBusinessId}`)
      .then((response) => {
        setLoading(false);
        const interval = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`);
        if (interval > 0) {
          timerLogic();
        }
        if (response.data.success) {
          setDataPoints(response.data.data);
        }
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  }

  function getBusiness() {
    setLoadingBusiness(true);
    api.get(`businesses/${businessId}`)
      .then((response) => {
        setLoadingBusiness(false);
        if (response.data.success) {
          setBusiness(response.data.data);
        }
      })
      .catch((error) => {
        setLoadingBusiness(false);
      });
  }

  function getCurrentUrl() {
    let url = `${window.location.origin}/reports/loss/targetvsactual?`;
    const params = [];
    if (selectedBusinessId?.length > 0) {
      params.push(`businessId=${selectedBusinessId}`);
    }
    params.push(`showTotals=${!hideTotals}`);
    params.push(`type=${selectedType}`);
    params.push(`lossType=${selectedLossType}`);
    params.push(`timer=${refreshTimer}`);
    url += params.join('&');
    navigator.clipboard.writeText(url);
    showMessage(t('link_copied_to_clipboard'));
  }

  function timerLogic() {
    const interval = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`);
    const lastTimeoutId = window.localStorage.getItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timerId`);
    if (lastTimeoutId) {
      window.clearInterval(lastTimeoutId);
    }

    if (interval > 0) {
      const tempId = setTimeout(() => {
        if (!loading) {
          if (!loading) {
            getData();
          }
          timerLogic();
        }
      }, interval * 60000);
      window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timerId`, tempId);
    }
  }

  useEffect(() => {
    if (businessId) {
      getBusiness();
    }
    setTimeout(() => {
      setIsInitialized(true);
    }, 1000);
    window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`, timer ? parseInt(timer, 10) : 0);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      window.localStorage.setItem(`${envConfig.REACT_APP_PREFIX}-loss-report-timer`, refreshTimer);
      setTimeout(() => timerLogic(), 1000);
    }
  }, [refreshTimer]);

  useEffect(() => {
    getData();
    if (isInitialized) {
      timerLogic();
    }
  }, [selectedType, dates]);

  return (
    <div>
      <PageHeader
        className="mb-4 px-0"
        title={(
          <div className="space-x-2">
            <span>{t('loss')}: {t('participation')}</span>
            <Popover content={(
              <div>
                <p className="font-bold">{t('legend')}</p>
                <div>{t('loss_cost')} = ({t('movement_amount')} * {t('item_cost')})</div>
                <div>{t('loss_amount')} = {t('movement_amount')}</div>
              </div>
            )}>
              <FontAwesomeIcon icon={faInfoCircle} />
            </Popover>
          </div>
        )}
        extra={[
          <div className="space-x-1">
            <strong>{t('show_totals')}</strong>
            <Switch
              checked={!hideTotals}
              onChange={(obj) => setHideTotals(!obj)}
              disabled={loading || !profile}
            />
          </div>,
          <Select
            value={selectedBusinessId}
            style={{ width: 200 }}
            disabled={businesses?.state === 'loading' || businesses.state === 'hasError' || loadingBusiness}
            loading={businesses?.state === 'loading' || loadingBusiness}
            onChange={(obj) => setSelectedBusinessId(obj)}
          >
            <Option value="">{t('global')}</Option>
            {businesses.state === 'hasValue' && (
              <>
                {_.map(_.sortBy(businesses.contents.data.data.results, (s) => s.name, 'asc'), (b) => (
                  <Option
                    value={b.id}
                    key={b.id}
                  >
                    {b.name}
                  </Option>
                ))}
              </>
            )}
            {business && (
              <Option value={business?.id}>{business?.name}</Option>
            )}
          </Select>,
          <Select
            value={selectedLossType}
            style={{ width: 100 }}
            loading={loading}
            onChange={(obj) => setSelectedLossType(obj)}
          >
            <Option value="cost">{t('cost')}</Option>
            <Option value="amount">{t('amount')}</Option>
          </Select>,
          <RangePicker
            format="YYYY-MM-DD"
            value={[
              dayjs(dates?.start),
              dayjs(dates?.end)
            ]}
            presets={dateRangePresets(t)}
            onChange={(date, dateString) => {
              setDates({
                start: dayjs(dateString[0]),
                end: dayjs(dateString[1])
              });
            }}
          />,
          <Select
            onChange={(value) => setSelectedType(value)}
            value={selectedType}
            className="h-10 lg:w-56"
            disabled={loading}
          >
            <Option
              key="0"
              value="0"
            >
              {t('division')}
            </Option>
            <Option
              key="1"
              value="1"
            >
              {t('department')}
            </Option>
            <Option
              key="2"
              value="2"
            >
              {t('category')}
            </Option>
          </Select>,
          <Select
            value={refreshTimer}
            style={{ width: 200 }}
            loading={loading}
            onChange={(obj) => setRefreshTimer(obj)}
          >
            <Option value={0}>{t('no_refresh')}</Option>
            <Option value={5}>{t('5_min_refresh')}</Option>
            <Option value={10}>{t('10_min_refresh')}</Option>
            <Option value={15}>{t('15_min_refresh')}</Option>
            <Option value={30}>{t('30_min_refresh')}</Option>
            <Option value={60}>{t('60_min_refresh')}</Option>
          </Select>,
          <Button
            size="small"
            type="primary"
            onClick={() => getData()}
          >
            {t('reload')}
          </Button>
        ]}
      />
      {profile && (
        <div className="flex">
          <Button
            size="small"
            type="primary"
            className="ml-auto"
            onClick={() => getCurrentUrl()}
          >
            {t('get_url_for_report')}
          </Button>
        </div>
      )}
      <div>
        <strong>{renderShortDate(dates?.start.toString(), timezone)} - {renderShortDate(dates?.end.toString(), timezone)}</strong>
      </div>
      <Spin spinning={loading}>
        <div className="grid grid-cols-1">
          <div className="col-span-1" style={{ height: '74vh' }}>
            <Pie
              data={{
                labels: _.map(dataPoints, (s) => (i18n.language === 'en' ? t(s.nameEng) : t(s.name))),
                datasets: [
                  {
                    label: t('loss'),
                    backgroundColor: _.map(dataPoints, (x) => `${randomColor()}cc`),
                    borderWidth: 1,
                    data: _.map(dataPoints, (x) => (selectedLossType === 'cost' ? x.amountCost : x.amount)),
                  }
                ],
              }}
              options={{
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                    labels: {
                      font: {
                        size: 20
                      }
                    }
                  },
                  title: {
                    display: false,
                  },
                  datalabels: {
                    color: 'black',
                    formatter: (value, context) => (!hideTotals ? toCurrency(value) : ''),
                    labels: {
                      value: {
                        font: {
                          size: 20
                        }
                      }
                    }
                  }
                },
              }}
            />
          </div>
          <div className={`col-span-1 ${hideTotals ? 'hidden' : ''}`}>
            <Table
              dataSource={dataPoints}
              bordered
              columns={[
                {
                  title: t('grouping'),
                  key: 'grouping',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => (i18n.language === 'en' ? a.nameEng.localeCompare(b.nameEng) : a.name.localeCompare(b.name)),
                  render: (row) => <span>{row.name !== 'no_division' ? (i18n.language === 'en' ? row.nameEng : row.name) : t('no_division')}</span>,
                },
                {
                  title: t('loss'),
                  key: 'loss',
                  align: 'center',
                  className: 'text-xs',
                  sorter: (a, b) => (selectedLossType === 'cost' ? a.amountCost <= b.amountCost : a.amount <= b.amount),
                  render: (row) => <span>{selectedLossType === 'cost' ? toCurrency(row.amountCost) : thousand(row.amount)}</span>,
                },
                {
                  title: t('zero_cost_items'),
                  key: 'zeroCostItems',
                  align: 'center',
                  className: `text-xs ${selectedLossType === 'cost' ? '' : 'hidden'}`,
                  sorter: (a, b) => a.zeroCostItems <= b.zeroCostItems,
                  render: (row) => <span>{thousand(row.zeroCostItems)}</span>,
                },
              ]}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
}

export default LossParticipation;
